/**
 * InstallBanner — barra proactiva de instalación PWA.
 * Aparece cuando el browser dispara `beforeinstallprompt` (Android/Chrome/Edge).
 * Se descarta para la sesión actual; no molesta en iOS (que no tiene el evento).
 */
import { useState, useEffect } from "react";
import { Download, X } from "../lib/icons.jsx";

const esIOS = () => typeof navigator !== "undefined" && (/iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
const instalada = () => typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true);
const CLAVE_IOS = "crew_ios_instalar_hasta";

/** iPhone/iPad no dispara `beforeinstallprompt`: se instala a mano desde Compartir. Sin instalar no hay avisos push en iOS. */
function GuiaIOS() {
  const [ver, setVer] = useState(() => {
    if (!esIOS() || instalada()) return false;
    try { return Number(localStorage.getItem(CLAVE_IOS) || 0) < Date.now(); } catch { return true; }
  });
  if (!ver) return null;
  const cerrar = () => { try { localStorage.setItem(CLAVE_IOS, String(Date.now() + 14 * 24 * 3600 * 1000)); } catch { /* sin storage */ } setVer(false); };
  return (
    <div role="region" aria-label="Instalar en iPhone"
         className="fixed inset-x-3 bottom-[100px] z-[9998] rounded-[20px] bg-card p-4 text-foreground shadow-[0_4px_16px_rgba(17,19,24,0.18)]"
         style={{ animation: "swBannerIn 0.3s ease-out", marginBottom: "env(safe-area-inset-bottom)" }}>
      <div className="flex items-start gap-3">
        <span className="msr mt-0.5 text-[24px] text-[#0E7490]" aria-hidden="true">share</span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-extrabold leading-tight">Instalá SOA 10 PRO en tu iPhone</p>
          <p className="mt-1 text-[13px] leading-snug text-muted-foreground">Tocá <strong>Compartir</strong> (el cuadrado con la flecha) y elegí <strong>«Agregar a pantalla de inicio»</strong>. Así se abre como app y podés recibir avisos.</p>
        </div>
        <button onClick={cerrar} aria-label="Cerrar" className="text-muted-foreground"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export function InstallBanner() {
  if (esIOS()) return <GuiaIOS />;
  return <InstallBannerAndroid />;
}

function InstallBannerAndroid() {
  const [prompt,    setPrompt]    = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Ya descartado esta sesión
    if (sessionStorage.getItem("pwa-install-dismissed")) { setDismissed(true); return; }
    // Ya instalada
    if (window.matchMedia("(display-mode: standalone)").matches) { setInstalled(true); return; }

    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => setInstalled(true);

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!prompt || dismissed || installed) return null;

  const handleInstall = async () => {
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setPrompt(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("pwa-install-dismissed", "1");
  };

  return (
    // SOA 10 PRO: tarjeta clara con los tokens del sistema (antes una píldora negra con cian suelto).
    <div role="banner"
         className="fixed bottom-[100px] md:bottom-5 left-1/2 -translate-x-1/2 z-[9998] flex items-center gap-3 max-w-[calc(100vw-24px)] bg-card text-foreground text-[14px] px-4 py-2.5 rounded-[16px] border border-border shadow-[0_4px_16px_rgba(17,19,24,0.14)]"
         style={{ animation: "swBannerIn 0.3s ease-out" }}>
      <Download className="h-4 w-4 text-accent shrink-0" />
      <span className="font-semibold whitespace-nowrap">Instalá SOA 10 PRO en tu dispositivo</span>
      <button
        onClick={handleInstall}
        className="px-3.5 py-1.5 bg-foreground hover:opacity-90 text-background rounded-full text-[13px] font-bold transition-opacity whitespace-nowrap">
        Instalar
      </button>
      <button
        onClick={handleDismiss}
        aria-label="Cerrar"
        className="text-muted-foreground hover:text-foreground transition-colors ml-1">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
